/* global Vue, VueRouter, CONTEXT, AZURE_URI, LOCALE */

import Test from './views/Test'
import Dashboard from './views/Dashboard'
import Interests from './views/Interests'
import Preferences from './views/Preferences'
import Awards from './views/Awards'
import ObtainPoints from './views/ObtainPoints'
import TransferPoints from './views/TransferPoints'
import MyProfile from './views/MyProfile'
import Activity from './views/Activity'
import MainTitle from './components/MainTitle'
import { Notifications, useState, setSuccessAndStopLoading, setErrorAndStopLoading, startLoading, stopLoading } from '../apps/components/Notifications'
import ReservationsPage from './components/ReservationsPage'
import Error403 from './components/Error403'
import { messagesSpecific } from '../i18n/translations'
import { CreateI18n } from '../i18n/create-i18n'
import '../validation-rules'
import AuthService from '../AuthService'
import { isDebugEnabled } from '../utils-common'

const i18n = CreateI18n(messagesSpecific)

const router = VueRouter.createRouter({
  history: VueRouter.createWebHashHistory(),
  routes: [
    { path: '/', name: 'Dashboard', component: Dashboard, meta: { title: 'dashboard.title' } },
    {
      path: '/test',
      name: 'Test',
      component: Test,
      meta: { title: 'Test' },
      beforeEnter: () => { return isDebugEnabled.value || (CONTEXT !== 'production') },
    },
    { path: '/profile', name: 'Profile', component: MyProfile, meta: { title: 'myProfile.title' } },
    { path: '/profile/interests', name: 'Interests', component: Interests, meta: { title: 'interestsTab.title' } },
    { path: '/profile/preferences', name: 'Preferences', component: Preferences, meta: { title: 'preferencesTab.title' } },
    { path: '/awards', name: 'AllAwards', component: Awards, meta: { title: 'awards.title' } },
    { path: '/awards/recommended', name: 'RecommendedAwards', component: Awards, meta: { title: 'awards.title' } },
    { path: '/reservations', name: 'ReservationsActive', component: ReservationsPage, props: { active: true, limit: 50 }, meta: { title: 'reservations.title' } },
    { path: '/reservations/past', name: 'ReservationsPast', component: ReservationsPage, props: { active: false, limit: 50 }, meta: { title: 'reservations.title' } },
    { path: '/activity', name: 'PointsActivity', component: Activity, meta: { title: 'activity.title' } },
    { path: '/points/obtain', name: 'ObtainPoints', component: ObtainPoints, meta: { title: 'obtainPoints.mainTitle' } },
    { path: '/points/transfer', name: 'TransferPoints', component: TransferPoints, meta: { title: 'transferPoints.mainTitle' } },
    { path: '/:pathMatch(.*)', component: { template: '<h1 style="color: red">Page Not Found</h1>' } },
  ],
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { left: 0, top: 0 }
    }
  },
})

const LoyaltyApp = Vue.createApp({
  name: 'LoyaltyApp',
  components: { ReservationsPage, MainTitle, Error403, Notifications },
  template: `
  <Notifications />
  <div v-show="$route.name !== 'Dashboard'" class="header-hybrid-padding">
    <MainTitle
      :title="$t($route.meta.title ?? 'loading')"
      :backText="($route.name === 'ObtainPoints' || $route.name === 'TransferPoints') ?  $t('activity.title') : $t('back')"
      :backRoute="($route.name === 'ObtainPoints' || $route.name === 'TransferPoints') ? { name: 'PointsActivity' } : { name: 'Dashboard' }"
      class="main-title--bg no-margin"
    />
  </div>

  <div v-show="$route.path.startsWith('/awards') && loyaltyId" class="tabs tabs--loyalty container">
    <div class="tabs__thumbs swiper">
      <div class="swiper-wrapper">
        <div
          class="swiper-slide"
          :class="{ 'swiper-slide-active': $route.name === 'AllAwards' }"
          @click="router.push({ name: 'AllAwards' })">
          <span>{{ $t('awards.all') }}</span>
        </div>
        <div
          class="swiper-slide"
          :class="{ 'swiper-slide-active': $route.name === 'RecommendedAwards' }"
          @click="router.push({ name: 'RecommendedAwards' })">
          <span>{{ $t('awards.recommended') }}</span>
        </div>
      </div>
    </div>
  </div>

  <div v-show="$route.path.startsWith('/profile') && loyaltyId" class="container grid">
    <div class="tabs tabs--loyalty col-12 col-md-10 offset-md-1 col-xl-8 offset-xl-2">
      <div class="tabs__thumbs swiper">
        <div class="swiper-wrapper">
          <div
            class="swiper-slide"
            :class="{ 'swiper-slide-active': $route.name === 'Profile' }"
            @click="router.push({ name: 'Profile' })">
            <span>{{ $t('dashboard.personalDetails') }}</span>
          </div>
          <div
            class="swiper-slide"
            :class="{ 'swiper-slide-active': $route.name === 'Interests' }"
            @click="router.push({ name: 'Interests' })">
            <span>{{ $t('interests') }}</span>
          </div>
          <div
            class="swiper-slide"
            :class="{ 'swiper-slide-active': $route.name === 'Preferences' }"
            @click="router.push({ name: 'Preferences' })">
            <span>{{ $t('preferencesTab.title') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-show="$route.path.startsWith('/reservations') && loyaltyId" class="tabs tabs--loyalty container">
    <div class="tabs__thumbs swiper">
      <div class="swiper-wrapper">
        <div
          class="swiper-slide"
          :class="{ 'swiper-slide-active': $route.name === 'ReservationsActive' }"
          @click="router.push({ name: 'ReservationsActive' })">
          <span>{{ $t('reservations.activeReservations') }}</span>
        </div>
        <div
          class="swiper-slide"
          :class="{ 'swiper-slide-active': $route.name === 'ReservationsPast' }"
          @click="router.push({ name: 'ReservationsPast' })">
          <span>{{ $t('reservations.pastReservations') }}</span>
        </div>
      </div>
    </div>
  </div>

  <router-view v-if="loyaltyId" :loyaltyId="loyaltyId" :properties="properties" />

  <Error403 v-show="authIsFinished && !state.isLoading.value && loyaltyId < 1" />
`,
  setup () {
    const { state } = useState()
    const loyaltyId = Vue.ref(0)
    const authIsFinished = Vue.ref(false)

    const header = document.querySelector('.header')
    const logo = document.querySelector('.header-logos')
    const logoImg = document.querySelector('.header-logos__maistra img')
    const originalLogo = logoImg.src
    const stickyLogo = document.querySelector('.header-logos__maistra').dataset.sticky
    const showOnDashEls = document.querySelectorAll('.js-show-on-dash')
    const rewardsEL = document.querySelector('.js-rewards')
    const properties = Vue.reactive([])

    function makeHeaderDark () {
      header.classList.add('header--dark')
      logo.classList.remove('header-logos--invert')
      logoImg.src = originalLogo
      window.PREVENT_LOGO_CHANGE = false
      showOnDashEls.forEach(el => {
        el.classList.remove('is-hidden')
      })
    }

    function makeHeaderLight () {
      header.classList.remove('header--dark')
      logo.classList.add('header-logos--invert')
      logoImg.src = stickyLogo
      window.PREVENT_LOGO_CHANGE = true
      showOnDashEls.forEach(el => {
        el.classList.add('is-hidden')
      })
    }

    router.afterEach((to, from) => {
      if (to.path === '/') {
        loyaltyId.value > 0 ? makeHeaderDark() : makeHeaderLight()

        Vue.watch(loyaltyId, (id) => {
          id > 0 ? makeHeaderDark() : makeHeaderLight()
        })
      } else {
        makeHeaderLight()
      }

      if ((to.path === '/activity' || to.path.includes('/reservations')) && rewardsEL) {
        rewardsEL.classList.remove('is-hidden')
      }
    })

    async function getProperties () {
      try {
        Object.assign(properties, null)
        const response = await fetch(`${AZURE_URI}GetProperties?locale=${LOCALE}`)
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        Object.assign(properties, await response.json())
        properties.sort((a, b) => (a.propertyListingWeight ?? Number.MAX_VALUE) - (b.propertyListingWeight ?? Number.MAX_VALUE))
      } catch (err) {
        setErrorAndStopLoading(err)
      }
    }

    Vue.onMounted(() => {
      getProperties()
      startLoading('loyalty-app')
      AuthService.on('ended', () => {
        if (AuthService.accountId === undefined) {
          stopLoading('loyalty-app')
          authIsFinished.value = true
        }
      })
      authIsFinished.value = AuthService.isFinished
      if (authIsFinished.value) {
        stopLoading('loyalty-app')
      }
      AuthService.on('authenticated', e => {
        stopLoading('loyalty-app')
        try {
          if (Number.isInteger(e?.detail.loyaltyId)) {
            loyaltyId.value = e.detail.loyaltyId
          } else {
            makeHeaderLight()
            throw Error('no loyaltyID')
          }
        } catch (e) {
          makeHeaderLight()
          setErrorAndStopLoading(e, true)
        }
        authIsFinished.value = true
      })
    })

    return { state, setSuccessAndStopLoading, setErrorAndStopLoading, router, loyaltyId, authIsFinished, properties }
  },
})

const appElement = document.getElementById('loyalty-app')

if (appElement) {
  LoyaltyApp.config.globalProperties.$AuthService = AuthService // same service is importent on all apps
  LoyaltyApp.use(i18n)
  LoyaltyApp.use(router)
  LoyaltyApp.mount(appElement)
}

export default LoyaltyApp
